import { QREncodeURI } from "@services";
import { ESCCommands } from "@utils/escpos";

const COLUMN_WIDTH = 8;
const PAD_CHAR = " "
const MAX_PAPER_WIDTH = 48;

/**
 * Finds the width of a line of text.
 * Usually, this is the length of the line.
 * However, if the line contains a \t character, the cursor moves to the next tab stop.
 * The tab stops are set at every 8 characters.
 */
const findLineWidth = (str) => {
  const tabs = str.split("\t");
  let width = 0;
  tabs.forEach((tab, index) => {
    if (index > 0) {
      width += COLUMN_WIDTH - (width % COLUMN_WIDTH);
    }
    width += tab.length;
  });
  return width;
}

const padString = (str, len, alignment, stringLength) => {
  const strLen = stringLength ?? str.length;
  if (strLen >= len) {
    return str;
  }
  const pad = len - strLen;
  let padStr = "";
  if (alignment === "left") {
    padStr = str + PAD_CHAR.repeat(pad);
  } else if (alignment === "right") {
    padStr = PAD_CHAR.repeat(pad) + str;
  } else if (alignment === "center") {
    const leftPad = Math.floor(pad / 2);
    const rightPad = pad - leftPad;
    padStr = PAD_CHAR.repeat(leftPad) + str + PAD_CHAR.repeat(rightPad);
  }

  return padStr;
}

export class Ticket {
  constructor() {
    this.title = null;
    this.sections = []; // array of TicketSection
    this.paperWidth = 48;
  }

  addSection(section) {
    this.sections.push(section);
  }

  get width() {
    return Math.max(...this.sections.map( section => section.width));
  }

  html() {
    return TicketRenderer.toHTML(this);
  }

  escPos() {
      return TicketRenderer.toEscPos(this, this.paperWidth);
  }
}

export class TicketSection {
  constructor({ heading = null, lines = [], headingSize = 'small', align = 'center' }) {
    this.heading = heading; // optional heading (e.g. group name)
    this.lines = lines; // array of TicketLine
    this.headingSize = headingSize; // optional heading size (e.g. small, medium, large)
    this.align = align; // optional alignment (e.g. left, center, right)
  }

  get width() {
    return this.lines.reduce((acc, line) => {
      return Math.max(acc, line.width);
    }, 0);
  }

  toEscPos() {
    let cmds = [];
    if (this.heading) {
      cmds.push(ESCCommands.bold(true));
      cmds.push(ESCCommands.fontSize(this.headingSize));
      cmds.push(ESCCommands.text(this.heading));
      cmds.push(ESCCommands.fontSize("small"));
      cmds.push(ESCCommands.bold(false));
      cmds.push(ESCCommands.feed(1));
    }

    if (this.width > MAX_PAPER_WIDTH) {
      cmds.push(ESCCommands.alignLeft());
    }

    this.lines?.forEach((line) => {
      line.paperWidth = this.paperWidth;
      cmds.push(...line.toEscPos());
    });

    cmds.push(ESCCommands.alignCenter());

    return cmds;
  }
}

export class TicketHeader {
  constructor({ title, subtitle }) {
    this.title = title; // The main title
    this.subtitle = subtitle; // The subtitle
  }

  get width() {
    return Math.max(findLineWidth(this.title), findLineWidth(this.subtitle));
  }

  toEscPos() {
    let cmds = [];
    cmds.push(ESCCommands.bold(true));
    cmds.push(ESCCommands.fontSize("medium"));
    cmds.push(ESCCommands.text(this.title));
    cmds.push(ESCCommands.bold(false));
    cmds.push(ESCCommands.fontSize("small"));
    if (this.subtitle) {
      cmds.push(ESCCommands.text(this.subtitle));
    }
    cmds.push(ESCCommands.feed(2));
    return cmds;
  }
} 

export class TicketLine {
  constructor({ quantity = 0, title, details = [] }) {
    this.quantity = quantity;
    this.title = title;
    this.details = details; // extra info like variants, notes
    this.paperWidth = 48; // default paper width
    this.align = "left"; // default alignment
  }

  get renderedTitle() {
    return this.quantity >= 1 ? `${this.quantity}\t${this.title}` : this.title;
  }

  get renderedDetails() {
    const str = this.details.join(" · ");
    return this.quantity >= 1 ? `\t${str}` : str;
  }

  get width () {
    // Gets the width of the line, which is the max of the title and details
    return Math.max(
      findLineWidth(this.renderedTitle),
      findLineWidth(this.renderedDetails)
    );
  }

  toEscPos() {
    let cmds = [];
    
    cmds.push(ESCCommands.bold(true));
    cmds.push(ESCCommands.text(padString(this.renderedTitle, this.paperWidth, this.align, findLineWidth(this.renderedTitle))));
    cmds.push(ESCCommands.bold(false));

    if (this.details?.length) {
      cmds.push(
        ESCCommands.text(
          padString(
            this.renderedDetails,
            this.paperWidth,
            this.align,
            findLineWidth(this.renderedDetails)
          )
        )
      );
    }
    cmds.push(ESCCommands.feed(1));

    
    return cmds;
  }
}

export class TicketQRCode {
  constructor(data, {title, subtitle, bottomText} = {}) {
    this.data = data; // The QR content
    this.title = title || ''; // The main title
    this.subtitle = subtitle; // The subtitle
    this.bottomText = bottomText; // The bottom text
  }

  get width() {
    return 0;
  }

  toEscPos() {
    let cmds = [];
    cmds.push(ESCCommands.feed(4));
    cmds.push(ESCCommands.cut());

    cmds.push(ESCCommands.bold(true));
    cmds.push(ESCCommands.fontSize("medium"));
    cmds.push(ESCCommands.text(this.title));
    cmds.push(ESCCommands.fontSize("small"));
    cmds.push(ESCCommands.bold(false));

    if (this.subtitle) {
      cmds.push(ESCCommands.text(this.subtitle));
    }

    cmds.push(ESCCommands.feed(1));
    
    cmds.push(ESCCommands.qrCode(this.data));
    cmds.push(ESCCommands.feed(1));

    if (this.bottomText) {
      cmds.push(ESCCommands.bold(true));
      cmds.push(ESCCommands.fontSize("medium"));
      cmds.push(ESCCommands.text(this.bottomText));
      cmds.push(ESCCommands.bold(false));
      cmds.push(ESCCommands.fontSize("small"));
    }
    return cmds;
  }
}

export class TicketHR {
  constructor(type) {
    this.type = type; // The type of the HR (e.g. "dashed", "solid")
  }

  get width() {
    return 0;
  }
}

// Each row contains two texts,
// Right column will be padded to fill the space
export class TicketTable {
  constructor({ rows = [] }) {
    this.rows = rows; // array of TicketLine
    this.paperWidth = 48; // default paper width
  }

  get width() {
    return this.rows.reduce((acc, row) => {
      const leftWidth = findLineWidth(row[0]);
      const rightWidth = findLineWidth(row[1]);
      return Math.max(acc, leftWidth + rightWidth + 4); // 4 is minimum padding between columns
    }, 0);
  }

  toEscPos() {
    const cmds = [
      ESCCommands.bold(true),
      ESCCommands.text( "_".repeat(this.paperWidth)),
      ESCCommands.feed(1),
      ESCCommands.bold(false),
    ];
    
    this.rows.forEach((row) => {
      const leftText = row[0];
      const rightText = row[1];
      const paddedRightText = padString(
        rightText,
        this.paperWidth - leftText.length,
        "right"
      );
      cmds.push(ESCCommands.text(leftText + paddedRightText));
    });
    cmds.push(ESCCommands.text(""))
    return cmds
  }
}

export class TicketRenderer {
  static toHTML(ticket) {
    const parts = [];
    if (ticket.title) {
      parts.push(`<p id="title"><span>${ticket.title}</span></p>`);
    }

    ticket.sections.forEach((section) => {
      if (section instanceof TicketQRCode) {
        parts.push(
          `<div style="text-align: center"><img src="${QREncodeURI([
            section.data,
          ])}" style="max-width: 42mm"></div><hr>`
        );
      } else if (section instanceof TicketSection) {
        if (section.heading) {
          parts.push(
            `<p style="font-weight: bold; font-size: 1.3rem;">${section.heading}</p>`
          );
        }
        parts.push("<table>");
        parts.push("<tbody>");
        console.log(parts, ticket);
        section.lines?.forEach((line) => {
          parts.push(`
                        <tr>
                            <td style="font-size: 1.1rem;">${line.quantity}</td>
                            <td style="text-align: left; font-size: 1.1rem;">
                                <strong>${line.title}</strong>
                                ${
                                  line.details.length
                                    ? `<div>${line.renderedDetails}</div>`
                                    : ""
                                }
                            </td>
                        </tr>
                    `);
        });
        parts.push("</tbody>");
        parts.push("</table>");
      }
    });

    return parts.join("\n");
  }

  static toEscPos(ticket, paperWidth) {
    const cmds = [];
    cmds.push(ESCCommands.setUTF8());
    cmds.push(ESCCommands.alignCenter());
    if (ticket.title) {
      cmds.push(ESCCommands.bold(true));
      cmds.push(ESCCommands.fontSize("large"));
      cmds.push(ESCCommands.text(ticket.title));
      cmds.push(ESCCommands.bold(false));
      cmds.push(ESCCommands.fontSize("small"));
      cmds.push(ESCCommands.feed(1));
    }

    paperWidth = Math.min(MAX_PAPER_WIDTH, Math.max(40, ticket.width))

    ticket.sections.forEach((section) => {
      section.paperWidth = paperWidth;
      if (section instanceof TicketQRCode) {
        cmds.push(...section.toEscPos());
      } else if (section instanceof TicketHR) {
        cmds.push(ESCCommands.text(
          section.type === 'solid' ? '__'.repeat(paperWidth / 2) : '_ '.repeat(paperWidth / 2)
        ))
        cmds.push(ESCCommands.feed(1));
      } else if (section instanceof TicketSection) {
         cmds.push(...section.toEscPos());
      } else if (section instanceof TicketTable) {
        cmds.push(...section.toEscPos());
      } else if (section instanceof TicketHeader) {
        cmds.push(...section.toEscPos());
      }
    });

    // Feed a few lines
    cmds.push(ESCCommands.feed(4));
    cmds.push(ESCCommands.cut());

    return cmds.join("");
  }
}